import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

// Suppress ResizeObserver loop limit exceeded error
const resizeObserverErrorSuppressor = (e) => {
  if (e.message === 'ResizeObserver loop limit exceeded' || 
      e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.stopImmediatePropagation();
    return;
  }
};

window.addEventListener('error', resizeObserverErrorSuppressor);
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.message === 'ResizeObserver loop limit exceeded' ||
      e.reason?.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.preventDefault();
    return;
  }
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#174b6a',
      dark: '#0d3249',
      light: '#247BA0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#82c0c7',
      dark: '#5a9ca3',
      light: '#a8d5da',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#f4f6f8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#192529',
      secondary: '#555555',
    },
    grey: {
      100: '#f5f5f5',
      200: '#e8ecef',
      300: '#d0d8df',
      400: '#8aa8b7',
      500: '#6b8a99',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica Neue", sans-serif',
    h1: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400, letterSpacing: '-0.02em' },
    h2: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400, letterSpacing: '-0.01em' },
    h3: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.css-19lmypq': {
          marginLeft: '0 !important',
        },
        '.css-0': {
          minWidth: '1200px !important',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease-out',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(24, 99, 220, 0.24)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(24, 99, 220, 0.32)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.5s ease-out, transform 0.3s ease-out',
          '&:hover': {
            boxShadow: '0px 16px 24px -8px rgba(23, 75, 106, 0.15)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#174b6a',
          boxShadow: '0 2px 8px rgba(23, 75, 106, 0.16)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);